import { routeConstants } from 'app/routes/route-constants';
import { Configuration } from 'app/components/metro-line/types';
import { RefObject } from 'react';
import { UserEditSectionType } from 'app/area/administration/edit/user/types';
import { AdministrationEditTranslation } from 'app/area/administration/edit/constants';
import { PersonActionType } from 'app/area/administration/shared/types';
import { validationHighlights, validationPrefixHighlights } from 'app/area/administration/shared/helpers';
import { experienceTransferAlerts } from 'app/area/administration/constants';

// tslint:disable-next-line:max-func-body-length
export const metroLineConfiguration = (
  id: number,
  actionType: PersonActionType,
  displayAssignRoleSection: boolean,
  hasSubscriptions: boolean,
  detailsSectionRef: RefObject<HTMLDivElement>,
  accessToOperationSectionRef: RefObject<HTMLDivElement>,
  accessToScreenSectionRef: RefObject<HTMLDivElement>,
  extendedAccessSectionRef: RefObject<HTMLDivElement>,
  restrictedAccessRef: RefObject<HTMLDivElement>,
  accessToAdministrateUserGroupRef: RefObject<HTMLDivElement>,
  accessToAssignRolesSectionRef: RefObject<HTMLDivElement>,
  defaultValuesSectionRef: RefObject<HTMLDivElement>,
  userOccurenceSectionRef: RefObject<HTMLDivElement>,
  notificationsRef: RefObject<HTMLDivElement>,
  experienceTransferAlertsRef: RefObject<HTMLDivElement>,
  validationErrors: { [key: string]: boolean }
): Configuration<UserEditSectionType>[] => {

  const filterInvisibles = (configurationItem: Configuration<UserEditSectionType>): boolean => configurationItem.visible;

  return [
    {
      section: UserEditSectionType.Details,
      visible: true,
      iconUrl: '/i/case-sections/default.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.Details),
      label: (translation: AdministrationEditTranslation) => translation.edit_details_title,
      ref: detailsSectionRef,
      isValid: validationHighlights(['name', 'personId', 'userType', 'mainUserGroup',
        'currentUserGroup', 'language', 'emailAddress', 'additionalInformation', 'alternativeIds'], validationErrors) &&
        validationPrefixHighlights(['alternativeId'], validationErrors)
    },
    {
      section: UserEditSectionType.AccessToOperation,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.AccessToOperation),
      label: (translation: AdministrationEditTranslation) => translation.operation,
      ref: accessToOperationSectionRef,
      isValid: validationHighlights(['accessOperations'], validationErrors)
    },
    {
      section: UserEditSectionType.AccessToScreen,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.AccessToScreen),
      label: (translation: AdministrationEditTranslation) => translation.access_to_screen,
      ref: accessToScreenSectionRef,
      isValid: validationHighlights(['accessToScreen'], validationErrors)
    },
    {
      section: UserEditSectionType.ExtendedAccess,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.ExtendedAccess),
      label: (translation: AdministrationEditTranslation) => translation.extended_access,
      ref: extendedAccessSectionRef,
      isValid: validationHighlights(['extendedAccess'], validationErrors)
    },
    {
      section: UserEditSectionType.RestrictedAccess,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.RestrictedAccess),
      label: (translation: AdministrationEditTranslation) => translation.restricted_access,
      ref: restrictedAccessRef,
      isValid: validationHighlights(['restrictedAccess'], validationErrors)
    },
    {
      section: UserEditSectionType.AccessToAdministrateUserGroup,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.AccessToAdministrateUserGroup),
      label: (translation: AdministrationEditTranslation) => translation.access_to_administrate_user_group,
      ref: accessToAdministrateUserGroupRef,
      isValid: validationHighlights(['accessToUserGroups'], validationErrors)
    },
    {
      section: UserEditSectionType.AccessToAssignRoles,
      visible: displayAssignRoleSection,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.AccessToAssignRoles),
      label: (translation: AdministrationEditTranslation) => translation.access_to_assign_roles_title,
      ref: accessToAssignRolesSectionRef,
      isValid: validationHighlights(['accessToAssignRoles'], validationErrors)
    },
    {
      section: UserEditSectionType.DefaultValues,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.DefaultValues),
      label: (translation: AdministrationEditTranslation) => translation.default_values,
      ref: defaultValuesSectionRef,
      isValid: validationHighlights(['defaultValues'], validationErrors)
    },
    {
      section: UserEditSectionType.UserOccurence,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.UserOccurence),
      label: (translation: AdministrationEditTranslation) => translation.user_occurrence_in_preson_field_lists,
      ref: userOccurenceSectionRef,
      isValid: validationHighlights(['personExcluded'], validationErrors)
    },
    {
      section: UserEditSectionType.Notifications,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.Notifications),
      label: (translation: AdministrationEditTranslation) => translation.responsible_for_unassigned_cases_and_actions,
      ref: notificationsRef,
      isValid: validationHighlights(['responsibleForUnits'], validationErrors)
    },
    {
      section: UserEditSectionType.ExperienceTransferAlerts,
      visible: !!experienceTransferAlerts && hasSubscriptions,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getScrollViewUrl(actionType, id, UserEditSectionType.ExperienceTransferAlerts),
      label: (translation: AdministrationEditTranslation) => translation.subscriber_groups,
      ref: experienceTransferAlertsRef,
      isValid: validationHighlights(['experienceTransferAlerts'], validationErrors)
    }
  ].filter(filterInvisibles);
};

const getScrollViewUrl = (actionType: PersonActionType, id: number, section: UserEditSectionType) => {
  return actionType && {
    [PersonActionType.add]: routeConstants.userAddSectionFor(section),
    [PersonActionType.edit]: routeConstants.userEditSectionFor(id, section),
    [PersonActionType.duplicate]: routeConstants.userDuplicateSectionFor(id, section)
  }[actionType];
};
